import { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Pause, Play, Monitor, Building2, ChevronDown, Check, LogOut, User, Shield, Globe, Settings } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useHeaderData } from '@/contexts/HeaderDataContext';
import { useNavigate } from 'react-router-dom';
import { TenantStatus, ROLE_LABELS } from '@/types';
import { cn } from '@/lib/utils';

interface HeaderProps {
  tenant: string;
  onTenantChange: (tenant: string) => void;
}

export function Header({ tenant, onTenantChange }: HeaderProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = hasRole('super_admin');

  const [tenantStatus, setTenantStatus] = useState<TenantStatus | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  // RBAC-aware org + agent data — from shared context so management pages can refresh
  const { orgs, agents } = useHeaderData();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  // Dropdown open state
  const [orgOpen, setOrgOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const orgRef = useRef<HTMLDivElement>(null);
  const agentRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on click outside
  useEffect(() => {
    if (!orgOpen && !agentOpen && !userMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (orgOpen && orgRef.current && !orgRef.current.contains(e.target as Node)) setOrgOpen(false);
      if (agentOpen && agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentOpen(false);
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [orgOpen, agentOpen, userMenuOpen]);

  // Derive the list of agents the user can see.
  // The API already scopes agents per role, but for super admin with org
  // filtering we narrow further on the client.
  const visibleAgents = useMemo(() => {
    if (isSuperAdmin && selectedOrgId) {
      return agents.filter(a => a.org_id === selectedOrgId);
    }
    return agents;
  }, [agents, selectedOrgId, isSuperAdmin]);

  // Current selected agent info — must use visibleAgents so an agent from
  // a different org is never shown when the selected org has no agents.
  const selectedAgent = visibleAgents.find(a => a.tenant_id === tenant);
  const selectedOrg = orgs.find(o => o.id === selectedOrgId);

  // Auto-select org from current tenant when orgs load
  useEffect(() => {
    if (isSuperAdmin && orgs.length > 0 && selectedAgent) {
      setSelectedOrgId(selectedAgent.org_id);
    }
  }, [orgs, selectedAgent, isSuperAdmin]);

  // If current tenant is not in visible agents AND there are agents to switch to,
  // auto-switch. But ONLY if no org filter is active or the filtered org has agents.
  // This prevents showing agents from a different org when the selected org has none.
  useEffect(() => {
    if (visibleAgents.length > 0 && !visibleAgents.find(a => a.tenant_id === tenant)) {
      onTenantChange(visibleAgents[0].tenant_id);
    }
    // Don't auto-switch when visible list is empty — that means the selected org
    // genuinely has no agents yet (e.g. freshly created). The selector will show
    // "No agents" instead of pulling in an agent from a different org.
  }, [visibleAgents, tenant, onTenantChange]);

  // Tenant status polling
  useEffect(() => {
    const load = () => {
      api.getTenantStatus()
        .then(statuses => {
          const found = statuses.find(s => s.tenant_id === tenant);
          if (found) setTenantStatus(found);
        })
        .catch(console.error);
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [tenant]);

  const isPaused = tenantStatus?.paused ?? false;

  const handleTogglePause = async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      if (isPaused) {
        await api.resumeTenant(tenant);
      } else {
        await api.pauseTenant(tenant);
      }
      const statuses = await api.getTenantStatus();
      const found = statuses.find(s => s.tenant_id === tenant);
      if (found) setTenantStatus(found);
    } catch (error) {
      console.error('Failed to toggle pause:', error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleOrgChange = (orgId: string) => {
    setSelectedOrgId(orgId);
    setOrgOpen(false);
    // Auto-switch to first agent in the new org (if it has agents)
    const orgAgents = agents.filter(a => a.org_id === orgId);
    if (orgAgents.length > 0) {
      onTenantChange(orgAgents[0].tenant_id);
    }
    // If org has no agents, we intentionally do NOT switch tenant.
    // The agent selector will show "No agents available".
  };

  const handleAgentChange = (tenantId: string) => {
    onTenantChange(tenantId);
    setAgentOpen(false);
  };

  const cycleTheme = () => {
    if (theme === 'dark') setTheme('light');
    else if (theme === 'light') setTheme('system');
    else setTheme('dark');
  };

  return (
    <header className="h-14 border-b border-border/50 bg-card/50 backdrop-blur-xl px-6 flex items-center justify-between shrink-0 z-30 shadow-[0_1px_3px_-1px_rgba(0,0,0,0.06)] dark:shadow-none">
      <div className="flex items-center gap-2">
        {/* Org Selector — Super Admin only */}
        {isSuperAdmin && orgs.length > 0 && (
          <div className="relative" ref={orgRef}>
            <button
              onClick={() => setOrgOpen(!orgOpen)}
              className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-muted/50 transition-colors"
            >
              <Globe className="w-4 h-4 text-sabio-purple" />
              <span className="text-sm font-medium text-foreground">{selectedOrg?.name || 'All Orgs'}</span>
              <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', orgOpen && 'rotate-180')} />
            </button>

            <AnimatePresence>
              {orgOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-1 min-w-[220px] bg-popover border border-border/50 rounded-xl shadow-xl overflow-hidden z-50"
                >
                  <div className="p-2 border-b border-border/50">
                    <p className="text-xs text-muted-foreground px-2">Select organisation</p>
                  </div>
                  <div className="py-1 max-h-48 overflow-y-auto">
                    {orgs.map(o => (
                      <button
                        key={o.id}
                        onClick={() => handleOrgChange(o.id)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50 transition-colors',
                          o.id === selectedOrgId && 'bg-primary/10'
                        )}
                      >
                        <Globe className={cn('w-4 h-4', o.id === selectedOrgId ? 'text-sabio-purple' : 'text-muted-foreground')} />
                        <span className={cn('flex-1 text-left', o.id === selectedOrgId && 'font-medium')}>{o.name}</span>
                        {o.id === selectedOrgId && <Check className="w-4 h-4 text-primary" />}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Divider between org and agent selectors */}
        {isSuperAdmin && orgs.length > 0 && (
          <div className="h-5 w-px bg-border/50" />
        )}

        {/* Agent Selector */}
        <div className="relative" ref={agentRef}>
          <button
            onClick={() => setAgentOpen(!agentOpen)}
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-muted/50 transition-colors"
          >
            <Building2 className="w-4 h-4 text-primary" />
            <span className={cn('text-sm font-medium', selectedAgent ? 'text-foreground' : 'text-muted-foreground')}>{selectedAgent?.name || (visibleAgents.length === 0 ? 'No agents' : tenant)}</span>
            <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', agentOpen && 'rotate-180')} />
          </button>

          <AnimatePresence>
            {agentOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 mt-1 min-w-[220px] bg-popover border border-border/50 rounded-xl shadow-xl overflow-hidden z-50"
              >
                <div className="p-2 border-b border-border/50">
                  <p className="text-xs text-muted-foreground px-2">Select agent</p>
                </div>
                <div className="py-1 max-h-48 overflow-y-auto">
                  {visibleAgents.map(a => (
                    <button
                      key={a.id}
                      onClick={() => handleAgentChange(a.tenant_id)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50 transition-colors',
                        a.tenant_id === tenant && 'bg-primary/10'
                      )}
                    >
                      <Building2 className={cn('w-4 h-4', a.tenant_id === tenant ? 'text-primary' : 'text-muted-foreground')} />
                      <span className={cn('flex-1 text-left', a.tenant_id === tenant && 'font-medium')}>{a.name}</span>
                      {a.tenant_id === tenant && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  ))}
                  {visibleAgents.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No agents available</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Pipeline status badge — Admin+ only, hidden until status loaded */}
        {hasRole('admin') && tenantStatus && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTogglePause}
                disabled={isToggling}
                className={isPaused
                  ? 'text-amber-500 hover:text-amber-400'
                  : 'text-green-500 hover:text-green-400'
                }
              >
                {isPaused ? <Pause className="w-4 h-4 mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />}
                <span className="text-xs font-medium">{isPaused ? 'Paused' : 'Active'}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isPaused ? 'Resume event collection' : 'Pause event collection'}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Theme toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={cycleTheme}>
              {theme === 'system' ? (
                <Monitor className="w-4 h-4" />
              ) : resolvedTheme === 'dark' ? (
                <Moon className="w-4 h-4" />
              ) : (
                <Sun className="w-4 h-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {theme === 'system' ? 'System theme' : theme === 'dark' ? 'Dark mode' : 'Light mode'}
          </TooltipContent>
        </Tooltip>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-muted/50 transition-colors ml-1"
          >
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-primary" />
            </div>
            {user && (
              <span className="text-sm font-medium text-foreground max-w-[120px] truncate hidden sm:block">
                {user.display_name || user.email}
              </span>
            )}
            <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', userMenuOpen && 'rotate-180')} />
          </button>

          <AnimatePresence>
            {userMenuOpen && user && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full right-0 mt-1 min-w-[220px] bg-popover border border-border/50 rounded-xl shadow-xl overflow-hidden z-50"
              >
                <div className="p-3 border-b border-border/50">
                  <p className="text-sm font-medium text-foreground truncate">{user.display_name || user.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Shield className="w-3 h-3 text-primary" />
                    <span className="text-xs text-primary font-medium">{ROLE_LABELS[user.role]}</span>
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Profile & Settings</span>
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}
